import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Navigation from './ZeronineHeader';
import Footer from './ZeronineFooter';
import { FileCheck } from 'lucide-react';
import { getSettingForWeb } from '../../redux/actions/package';

const TermsOfService = () => {
   const dispatch = useDispatch();
   const WebSetting = useSelector((state) => state.Package?.webSetting || []);

   useEffect(() => {
      dispatch(getSettingForWeb());
   }, [dispatch]);

   // Get terms content from WebSetting
   const termsContent = WebSetting[0]?.terms || '';

   return (
      <>
         <Navigation />
         <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pt-32 pb-20 px-4">
            <div className="max-w-4xl mx-auto">
               <div className="text-center mb-12">
                  <div className="inline-block px-4 py-2 bg-blue-100 rounded-full mb-6">
                     <span className="text-blue-600 font-bold">📄 LEGAL</span>
                  </div>
                  <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">Terms of Service</h1>
                  <p className="text-xl text-gray-600">Last updated: December 2024</p>
               </div>

               <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-gray-200">
                  {termsContent ? (
                     <div
                        className="prose prose-lg max-w-none"
                        dangerouslySetInnerHTML={{ __html: termsContent }}
                     />
                  ) : (
                     <div className="prose prose-lg max-w-none">
                        <div className="mb-8 p-6 bg-blue-50 rounded-2xl border border-blue-200">
                           <p className="text-blue-800 font-semibold">
                              Please read these terms carefully before using ZeroNine AI Accounting Software.
                           </p>
                        </div>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4 flex items-center gap-3">
                              <FileCheck className="w-8 h-8 text-blue-600" />
                              1. Agreement to Terms
                           </h2>
                           <p className="text-gray-700 mb-4 leading-relaxed">
                              By accessing or using ZeroNine AI Accounting Software, you agree to be bound by these Terms
                              of Service and all applicable laws and regulations. If you do not agree with any of these
                              terms, you are prohibited from using our services.
                           </p>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">2. Use License</h2>
                           <p className="text-gray-700 mb-4 leading-relaxed">
                              Permission is granted to temporarily use ZeroNine AI Accounting Software for personal or
                              business accounting purposes. This is the grant of a license, not a transfer of title, and
                              under this license you may not:
                           </p>
                           <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                              <li>Modify or copy the software materials</li>
                              <li>Use the materials for any commercial purpose or for any public display</li>
                              <li>Attempt to reverse engineer any software contained in ZeroNine AI</li>
                              <li>
                                 Transfer the materials to another person or "mirror" the materials on any other server
                              </li>
                           </ul>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">3. AI Features & Data Usage</h2>
                           <p className="text-gray-700 mb-4 leading-relaxed">
                              Our AI-powered features process your accounting data to provide insights, predictions, and
                              automation. By using our services, you grant us permission to:
                           </p>
                           <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                              <li>Process your financial data through our AI algorithms</li>
                              <li>Generate automated insights and recommendations</li>
                              <li>Use anonymized data to improve our AI models</li>
                              <li>Provide personalized accounting suggestions</li>
                           </ul>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">4. Account Registration</h2>
                           <p className="text-gray-700 mb-4 leading-relaxed">
                              You must register for an account and provide accurate, complete information. You are
                              responsible for maintaining the confidentiality of your account and password. You agree to
                              accept responsibility for all activities that occur under your account.
                           </p>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">5. Subscription & Payments</h2>
                           <p className="text-gray-700 mb-4 leading-relaxed">
                              Our services are offered on a subscription basis. You agree to pay all fees associated with
                              your subscription. Fees are non-refundable except as required by law or as stated in our
                              refund policy.
                           </p>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">6. GST Compliance</h2>
                           <p className="text-gray-700 mb-4 leading-relaxed">
                              While ZeroNine AI provides GST filing assistance and automated compliance features, you are
                              ultimately responsible for ensuring your GST filings are accurate and submitted on time. We
                              recommend consulting with a qualified tax professional for complex tax matters.
                           </p>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">7. Limitations</h2>
                           <p className="text-gray-700 mb-4 leading-relaxed">
                              In no event shall ZeroNine AI or its suppliers be liable for any damages arising out of the
                              use or inability to use our services, even if we have been notified of the possibility of
                              such damage.
                           </p>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">8. Termination</h2>
                           <p className="text-gray-700 mb-4 leading-relaxed">
                              We may terminate or suspend your account and access to our services immediately, without
                              prior notice, for conduct that we believe violates these Terms of Service or is harmful to
                              other users, us, or third parties, or for any other reason.
                           </p>
                        </section>

                        <section className="mb-8">
                           <h2 className="text-3xl font-black text-gray-900 mb-4">9. Contact Information</h2>
                           <p className="text-gray-700 leading-relaxed">
                              If you have any questions about these Terms of Service, please contact us at:
                           </p>
                           <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                              <p className="text-gray-700 font-semibold">Email: info@zeronine.ai</p>
                              <p className="text-gray-700 font-semibold">Phone: +91 72839-90909</p>
                              <p className="text-gray-700 font-semibold">
                                 Address: A-406 , A-Tower, Diamond World Mini Bajar , Varachha Road , Surat - 395006
                              </p>
                           </div>
                        </section>
                     </div>
                  )}
               </div>
            </div>
         </div>
         <Footer />
      </>
   );
};

export default TermsOfService;